/**
 * Centralized error handler for analytics tracking.
 * Reports errors to backend for monitoring and debugging.
 */

import { getStoredAnonymousId, getStoredAuthenticatedSessionId } from '@/lib/utils/analytics-session';

interface AnalyticsErrorData {
  error_type: string;
  error_message: string;
  error_stack?: string;
  error_source: 'frontend';
  session_id?: string;
  anonymous_id?: string;
  event_type?: string;
  event_data?: Record<string, unknown>;
  page_url?: string;
}

/**
 * Report an analytics error to the backend for monitoring
 */
export async function reportAnalyticsError(
  errorType: string,
  error: Error | string,
  context?: {
    eventType?: string;
    eventData?: Record<string, unknown>;
  }
): Promise<void> {
  try {
    const errorMessage = error instanceof Error ? error.message : error;
    const errorStack = error instanceof Error ? error.stack : undefined;
    const sessionId = getStoredAuthenticatedSessionId();
    const anonymousId = getStoredAnonymousId();

    const payload: AnalyticsErrorData = {
      error_type: errorType,
      error_message: errorMessage,
      error_source: 'frontend',
      page_url: window.location.pathname,
      ...(errorStack === undefined ? {} : { error_stack: errorStack }),
      ...(sessionId === null ? {} : { session_id: sessionId }),
      ...(anonymousId === null ? {} : { anonymous_id: anonymousId }),
      ...(context?.eventType === undefined ? {} : { event_type: context.eventType }),
      ...(context?.eventData === undefined ? {} : { event_data: context.eventData }),
    };

    // Use fetch directly to avoid circular dependencies
    await fetch('/api/v1/analytics/errors/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (reportingError: unknown) {
    console.error('[Analytics Error Handler] Failed to report error:', reportingError, errorType, error);
  }
}

/**
 * Wrap an async function with error reporting
 */
export function withAnalyticsErrorReporting<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  errorType: string
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      void reportAnalyticsError(errorType, error instanceof Error ? error : String(error));
      throw error;
    }
  }) as T;
}

/**
 * Safe wrapper for analytics operations that should never throw
 */
export async function safeAnalyticsCall<T>(
  operation: () => Promise<T>,
  errorType: string,
  context?: { eventType?: string; eventData?: Record<string, unknown> }
): Promise<T | null> {
  try {
    return await operation();
  } catch (error) {
    void reportAnalyticsError(errorType, error instanceof Error ? error : String(error), context);
    return null;
  }
}

export default {
  reportAnalyticsError,
  withAnalyticsErrorReporting,
  safeAnalyticsCall,
};
